import { Component, Input, OnInit, SimpleChanges, OnChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { AbstractControl, FormArray, FormArrayName, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, ValidationErrors, Validators } from '@angular/forms';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { CommonModule } from '@angular/common';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { ProposalsService } from 'src/app/services/api/proposals.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'proposals-payment-condition',
  imports: [InputFieldComponent,SelectFieldComponent,ReactiveFormsModule,CommonModule],
  templateUrl: './proposals-payment-condition.component.html',
  styleUrls: ['./proposals-payment-condition.component.scss']
})
export class ProposalsPaymentConditionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() readonlyPaymentCondition!: boolean;
  @Input() paymentCondition: any[] = []; 
  @Input() formPaymentCondition!: FormGroup; 
  @Output() paymentConditionsChange = new EventEmitter<any[]>();
  
  itemToBeDeleted!: FormArray;
  indexItemToBeDeleted: number = 0;
  showEmptyError: boolean = false;
  
  private destroy$ = new Subject<void>();
  private isPopulating = false;

  ratesOptions = [
    { key: '5', value: '5%' },
    { key: '10', value: '10%' },
    { key: '15', value: '15%' },
    { key: '20', value: '20%' },
    { key: '25', value: '25%' },
    { key: '30', value: '30%' },
    { key: '35', value: '35%' },
    { key: '40', value: '40%' },
    { key: '45', value: '45%' },
    { key: '50', value: '50%' },
    { key: '55', value: '55%' },
    { key: '60', value: '60%' },
    { key: '65', value: '65%' },
    { key: '70', value: '70%' },
    { key: '75', value: '75%' },
    { key: '80', value: '80%' },
    { key: '85', value: '85%' },
    { key: '90', value: '90%' },
    { key: '95', value: '95%' },
    { key: '100', value: '100%' }
  ];
  termsOptions = [
    { key: 'On receipt', value: 'On receipt' },
    { key: '30gg', value: '30gg'},
    { key: '60gg', value: '60gg'},
    { key: '90gg', value: '90gg'},
    { key: '120gg', value: '120gg'},
    { key: 'On the signing of this Agreement', value: 'On the signing of this Agreement' },    
    { key: 'When Services have been completed', value: 'When Services have been completed' }
  ];

  constructor(
    private fb: FormBuilder,
    private alertNotificationService: AlertNotificationService,
    private proposalsService: ProposalsService,
    private confirmationDialogService: ConfirmationDialogService
  ) {}

  totalPercentValidator(): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      if (!(formArray instanceof FormArray)) {
        return null;
      }
      
      if (formArray.controls.length === 0) {
        return null;
      }
      
      const hasAtLeastOnePercent = formArray.controls.some(control => {
        const percentValue = control.get('percent')?.value;
        return percentValue !== null && percentValue !== undefined && percentValue !== '';
      });
      
      if (!hasAtLeastOnePercent) {
        return null;
      }
      
      let totalPercent = 0;
      formArray.controls.forEach(control => {
        const percentValue = control.get('percent')?.value;
        if (percentValue) {
          totalPercent += Number(percentValue);
        }
      });
      
      if (totalPercent > 100) {
        return { totalPercentExceeded: { actual: totalPercent } };
      } else if (totalPercent < 100) {
        return { totalPercentInsufficient: { actual: totalPercent } };
      }
      
      return null;
    };
  }

  ngOnInit(): void {
    this.formPaymentCondition = this.fb.group({
      conditions: this.fb.array([], this.totalPercentValidator())
    });
    
    this.proposalsService.listPaymentConditionTypes().subscribe({
      next: (res: any) => {
        if (res?.data?.proposalPaymentConditionTypes) {
          this.termsOptions = res.data.proposalPaymentConditionTypes.map((term: any) => ({
            key: term.proposalPaymentConditionTypeId,
            value: term.name
          }));
          
          this.populateForm();
        }
      },
      error: (error) => {
        console.error('Errore nel caricamento delle opzioni dei termini:', error);
        
        this.populateForm();
      }
    });
    
    this.formPaymentCondition.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (!this.isPopulating) {
          this.emitPaymentConditions();
        }
      });
  }
  
  emitPaymentConditions(): void {
    if (!this.formPaymentCondition) return;
    
    const formArray = this.getPaymentConditionsArray();
    const conditions = formArray.controls.map((control: AbstractControl) => {
      const group = control as FormGroup;
      const conditionObj: any = {
        percent: this.parseNumericValue(group.get('percent')?.value),
        proposalPaymentConditionTypeId: this.parseNumericValue(group.get('proposalPaymentConditionTypeId')?.value),
        amount: this.parseNumericValue(group.get('partial')?.value)
      };
      const proposalPaymentConditionId = group.get('proposalPaymentConditionId')?.value;
      if (proposalPaymentConditionId) {
        conditionObj.proposalPaymentConditionId = this.parseNumericValue(proposalPaymentConditionId);
      }
      
      return conditionObj;
    });

    this.paymentConditionsChange.emit(conditions);
  }
  
  private parseNumericValue(value: any): number | any {
    if (value === null || value === undefined || value === '') {
      return value;
    }
    
    const parsedValue = Number(value);
    return !isNaN(parsedValue) ? parsedValue : value;
  }
  
  getTotalPercent(): number {
    const formArray = this.getPaymentConditionsArray();
    if (!formArray) return 0;
    
    let totalPercent = 0;
    formArray.controls.forEach(control => {
      const percentValue = control.get('percent')?.value;
      if (percentValue) {
        totalPercent += Number(percentValue);
      }
    });
    
    return totalPercent;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paymentCondition'] && 
        Array.isArray(changes['paymentCondition'].currentValue) && 
        this.termsOptions && this.termsOptions.length > 0) {
      this.populateForm();
    }
  }

  getPaymentConditionsArray(): FormArray {
    return this.formPaymentCondition?.get('conditions') as FormArray;
  }

  addNewPaymentCondition(): void {
    this.showEmptyError = false;
    const newCondition = this.createPaymentConditionFormGroup();
    newCondition.get('proposalPaymentConditionId')?.setValue(null);
    
    const formArray = this.getPaymentConditionsArray();
    formArray.push(newCondition);
    formArray.updateValueAndValidity();
    
    
    setTimeout(() => {
      this.emitPaymentConditions();
    }, 50);
  }

  removeCondition(formArray: FormArray, index: number): void {
    formArray.removeAt(index);
    formArray.updateValueAndValidity();
    this.alertNotificationService.success('Payment condition removed successfully');
    this.emitPaymentConditions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  confirmDelete(formArray: FormArray, index: number, item: string): void {
    const message = `Are you sure you want to delete this ${item}?`;
    this.itemToBeDeleted = formArray;
    this.indexItemToBeDeleted = index;
    this.confirmationDialogService.open(
      message,
      () => this.removeCondition(formArray, index)
    );
  }

  private findTermKey(termValue: any): string {
    if (!termValue) return '';
    
    const valueToFind = typeof termValue === 'object' && termValue.name ? termValue.name : termValue;
    
    const matchingOption = this.termsOptions.find(
      option => option.key === valueToFind || option.value === valueToFind
    );
    
    return matchingOption ? matchingOption.key : valueToFind;
  }
  
  private createPaymentConditionFormGroup(condition: any = {}): FormGroup {
    return this.fb.group({
      percent: [condition.percent || '', Validators.required],
      proposalPaymentConditionTypeId: [condition.proposalPaymentConditionTypeId || '', Validators.required],
      partial: [{value: condition.amount || '', disabled: true}],
      proposalPaymentConditionId: [condition.proposalPaymentConditionId || null]
    });
  }

  private populateForm(): void {
    this.isPopulating = true;
    const formArray = this.getPaymentConditionsArray();
    formArray?.clear();
    if (this.paymentCondition && Array.isArray(this.paymentCondition)) {
      let totalPercent = 0;
      this.paymentCondition.forEach(condition => {
        if (condition.percent) {
          totalPercent += Number(condition.percent);
        }
      });
      
      this.paymentCondition.forEach(condition => {
        const preparedCondition: any = {
          percent: condition.percent,
          amount: condition.amount,
          proposalPaymentConditionId: condition.proposalPaymentConditionId || null
        };

       if (condition.proposalPaymentConditionTypeId) {
          preparedCondition.proposalPaymentConditionTypeId = condition.proposalPaymentConditionTypeId;
        } else if (condition.proposalPaymentConditionType) {
          if (typeof condition.proposalPaymentConditionType === 'object' && condition.proposalPaymentConditionType?.proposalPaymentConditionTypeId) {
            preparedCondition.proposalPaymentConditionTypeId = condition.proposalPaymentConditionType.proposalPaymentConditionTypeId;
          } else if (typeof condition.proposalPaymentConditionType === 'string' || typeof condition.proposalPaymentConditionType === 'number') {
            preparedCondition.proposalPaymentConditionTypeId = condition.proposalPaymentConditionType;
          }
        } else if (condition.terms) {
          const matchingOption = this.termsOptions.find(
            option => option.key === condition.terms || option.value === condition.terms
          );
          
          if (matchingOption) {
            preparedCondition.proposalPaymentConditionTypeId = matchingOption.key;
          }
        }
        
        const conditionFormGroup = this.createPaymentConditionFormGroup(preparedCondition);
        
        if (!condition.proposalPaymentConditionId) {
          conditionFormGroup.get('proposalPaymentConditionId')?.setValue(null);
        }
        
        formArray?.push(conditionFormGroup);
      });
    }
    
    this.isPopulating = false;
    formArray?.updateValueAndValidity();
  }

  getFormControl(group: AbstractControl, controlName: string): FormControl {
    return (group as FormGroup).get(controlName) as FormControl;
  }

  isValid(): boolean {
    const array = this.getPaymentConditionsArray();
    if (!array || array.length === 0) {
      return false; 
    }
    
    return array.valid && array.controls.every(ctrl => ctrl.valid);
  }

  markAllAsTouched(): void {
    const array = this.getPaymentConditionsArray();
    if (!array) return;
    
    if (array.length === 0) {
      this.showEmptyError = true;
    }
    
    array.controls.forEach(ctrl => {
      (ctrl as FormGroup).markAllAsTouched();
      ctrl.updateValueAndValidity({ onlySelf: true });
    });
    array.updateValueAndValidity();
    
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];
    const array = this.getPaymentConditionsArray();
    
    if (!array || array.length === 0) {
      errors.push('At least one payment condition is required');
      return errors;
    }
    
    if (array.hasError('totalPercentExceeded')) {
      const totalPercent = array.getError('totalPercentExceeded').actual;
      errors.push(`Total payment percentage (${totalPercent}%) exceeds 100%. Please adjust the percentages to exactly 100%.`);
    } else if (array.hasError('totalPercentInsufficient')) {
      const totalPercent = array.getError('totalPercentInsufficient').actual;
      errors.push(`Total payment percentage (${totalPercent}%) is less than 100%. Please adjust the percentages to exactly 100%.`);
    }
    
    array.controls.forEach((ctrl, index) => {
      const group = ctrl as FormGroup;
      if (group.get('percent')?.invalid) {
        errors.push(`Proposal Condition row ${index + 1}: Percent is required`);
      }
      if (group.get('proposalPaymentConditionTypeId')?.invalid) {
        errors.push(`Proposal Condition row ${index + 1}: Terms is required`);
      }
      if (group.get('partial')?.invalid) {
        errors.push(`Proposal Condition row ${index + 1}: Amount is required`);
      }
    });
    return errors;
  }
}
